import { Link } from "react-router-dom";
import { useBrandSettings } from "@/hooks/useBrandSettings";
import { useCategoriesPublic } from "@/hooks/useCategoriesPublic";
import Logo from "./Logo";
import NewsletterBox from "./NewsletterBox";

const moreLinks = [
  { label: "Meinung", path: "/meinung" },
  { label: "Kolumnen", path: "/kolumnen" },
  { label: "Autoren", path: "/kolumnisten" },
  { label: "Suche", path: "/suche" },
];

const serviceLinks = [
  { label: "Kontakt", path: "/kontakt" },
  { label: "Über uns", path: "/ueber-uns" },
  { label: "Werbung", path: "/werbung" },
  { label: "Impressum", path: "/impressum" },
];

const legalLinks = [
  { label: "Datenschutz", path: "/datenschutz" },
  { label: "Cookie-Richtlinie", path: "/cookie-richtlinie" },
];

// Fallback links in case Supabase is not connected yet
const fallbackCategoryLinks = [
  { label: "Schweiz", path: "/schweiz" },
  { label: "Finanzen", path: "/finanzen" },
  { label: "Versicherung & Gesundheit", path: "/versicherung-gesundheit" },
  { label: "Technologie", path: "/technologie" },
  { label: "Digital & KI", path: "/digital-ki" },
  { label: "Arbeit & Leben", path: "/arbeit-leben" },
  { label: "Politik", path: "/politik" },
  { label: "Deals & Angebote", path: "/deals-angebote" },
];

export default function Footer() {
  const { settings } = useBrandSettings();
  const { menuCategories, loading: catsLoading } = useCategoriesPublic();

  const categoryLinks =
    menuCategories.length > 0
      ? menuCategories.map((c) => ({ label: c.name, path: `/${c.slug}` }))
      : fallbackCategoryLinks;

  return (
    <footer className="bg-hj-dark text-white">
      {/* Newsletter */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10">
          <NewsletterBox
            variant="inline"
            title="Bleiben Sie informiert."
            description="Erhalten Sie die wichtigsten Nachrichten aus der Schweiz direkt in Ihr Postfach."
          />
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-6">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Logo variant="footer" />
            <p className="text-sm text-gray-400 mt-3 max-w-sm leading-relaxed hidden sm:block">
              {settings.siteName} ist Ihre unabhängige Schweizer Nachrichtenplattform.
              Wir berichten über Politik, Finanzen, Gesundheits-Versicherung, Technologie
              und alles, was die Schweiz bewegt.
            </p>
            <div className="flex items-center gap-3 mt-4">
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                aria-label="Twitter"
              >
                <i className="ri-twitter-x-line text-sm"></i>
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                aria-label="Facebook"
              >
                <i className="ri-facebook-fill text-sm"></i>
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                aria-label="LinkedIn"
              >
                <i className="ri-linkedin-fill text-sm"></i>
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                aria-label="Instagram"
              >
                <i className="ri-instagram-line text-sm"></i>
              </a>
            </div>
          </div>

          {/* Categories */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider mb-3">Kategorien</h4>
            {catsLoading ? (
              <div className="space-y-2.5">
                <div className="h-4 w-28 bg-white/10 rounded animate-pulse"></div>
                <div className="h-4 w-24 bg-white/10 rounded animate-pulse"></div>
                <div className="h-4 w-32 bg-white/10 rounded animate-pulse"></div>
                <div className="h-4 w-20 bg-white/10 rounded animate-pulse"></div>
              </div>
            ) : (
              <ul className="space-y-2.5">
                {categoryLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* More */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider mb-3">Mehr</h4>
            <ul className="space-y-2.5">
              {moreLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service & Legal */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider mb-3">Service</h4>
            <ul className="space-y-1.5 mb-4">
              {serviceLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="text-xs font-semibold uppercase tracking-wider mb-3">Rechtliches</h4>
            <ul className="space-y-2.5">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-1">
          <p className="text-xs text-gray-500">
            &copy; {new Date().getFullYear()} {settings.siteName}. Alle Rechte vorbehalten.
          </p>
          <p className="text-xs text-gray-500">
            {settings.slogan}
          </p>
        </div>
      </div>
    </footer>
  );
}